import { Building2, Check, CornerUpLeft, Slash } from 'lucide-react'
import { Dropdown, Tooltip } from '@/components/ui'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import type { PatronRef } from '@/types'
import { usePatrons } from '../hooks'

/**
 * One action item's patron, with the three states it can be in.
 *
 * INHERITED is the default and is rendered differently on purpose: it is the
 * MEETING'S judgement, not a decision anybody made about this item. On a
 * meeting covering three patrons an inherited item shows all three and counts
 * against all three — the override is what makes it precise, and pretending
 * inheritance had picked one would be a lie the filter then contradicts.
 *
 * "No patron" is a real choice, distinct from inheriting nothing: the internal
 * runbook rewrite that came out of a patron meeting but is nobody's patron work.
 */
export function ActionItemPatron({
  patrons,
  isOverridden,
  onChange,
  disabled,
}: {
  patrons: PatronRef[]
  isOverridden: boolean
  onChange: (next: { inherit: true } | { patronId: string | null }) => void
  disabled?: boolean
}) {
  const t = useT()
  const { data: available } = usePatrons()

  const label = isOverridden
    ? patrons[0]?.name ?? t('actionPatron.none')
    : patrons.length === 0
      ? t('actionPatron.none')
      : patrons.map((c) => c.name).join(', ')

  return (
    <Dropdown
      heading={t('patronFilter.heading')}
      trigger={({ toggle }) => (
        <Tooltip
          content={
            isOverridden
              ? t('actionPatron.overriddenTip')
              : patrons.length > 1
                ? t('actionPatron.inheritedManyTip', { count: patrons.length })
                : t('actionPatron.inheritedTip')
          }
        >
          <button
            type="button"
            onClick={toggle}
            disabled={disabled}
            className={cn(
              'inline-flex max-w-[14rem] items-center gap-1 rounded-md px-1.5 py-0.5 text-sm',
              'hover:bg-surface-raised hover:text-fg disabled:opacity-50',
              isOverridden ? 'text-fg' : 'text-fg-subtle italic',
            )}
          >
            <Building2 aria-hidden className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{label}</span>
          </button>
        </Tooltip>
      )}
      items={[
        ...(isOverridden
          ? [
              {
                id: 'inherit',
                label: t('actionPatron.followMeeting'),
                icon: <CornerUpLeft />,
                onSelect: () => onChange({ inherit: true }),
              },
            ]
          : []),
        ...(available ?? []).map((patron) => ({
          id: patron.id,
          label: patron.name,
          icon: isOverridden && patrons[0]?.id === patron.id ? <Check /> : undefined,
          onSelect: () => onChange({ patronId: patron.id }),
        })),
        {
          id: 'none',
          label: t('actionPatron.none'),
          icon: <Slash />,
          separatorBefore: true,
          onSelect: () => onChange({ patronId: null }),
        },
      ]}
    />
  )
}
